/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPackage, FiBox, FiTag, FiAlertCircle, FiTrendingDown, FiSearch, FiDownload, FiFilter } from "react-icons/fi";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import * as XLSX from "xlsx";
import clsx from "clsx";

export default function InventoryReport() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [activeReport, setActiveReport] = useState("item-stock");
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemId, setItemId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [cardCounts, setCardCounts] = useState({});
  const [tagsList, setTagsList] = useState([]);

  const reportCards = [
    {
      id: "item-stock",
      endpoint: "/StockReports/item-stock",
      label_ar: "عدد منتج معينة مخزونة",
      label_en: "Item Stock",
      icon: <FiBox />,
      color: "#3b82f6", // Blue
      requiresItemId: true,
    },
    {
      id: "available-products",
      endpoint: "/StockReports/available-products",
      label_ar: "المنتجات المتوفرة",
      label_en: "Available Products",
      icon: <FiPackage />,
      color: "#10b981", // Green
    },
    {
      id: "brand-products",
      endpoint: "/StockReports/brand-products",
      label_ar: "عدد براند معين",
      label_en: "Brand Products",
      icon: <FiTag />,
      color: "#06b6d4", // Cyan
      requiresBrandId: true,
    },
    {
      id: "zero-stock",
      endpoint: "/StockReports/zero-stock",
      label_ar: "عدد المنتجات الصفرية",
      label_en: "Zero Stock",
      icon: <FiAlertCircle />,
      color: "#ef4444", // Red
    },
    {
      id: "low-stock",
      endpoint: "/StockReports/low-stock",
      label_ar: "المنتجات التي وصلت حد الطلب",
      label_en: "Low Stock",
      icon: <FiTrendingDown />,
      color: "#f59e0b", // Orange
    },
  ];

  const fetchStockReport = async () => {
    if (!perms.viewInventoryReport) {
      return;
    }
    if (!tenantId || tenantId === -1) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const currentReport = reportCards.find(r => r.id === activeReport);
      if (!currentReport) return;

      const token = localStorage.getItem("authToken");
      const url = new URL(`${baseUrl}${currentReport.endpoint}`);
      url.searchParams.append("tenantId", tenantId.toString());

      if (currentReport.requiresItemId) {
        url.searchParams.append("itemId", itemId || "-1");
      }
      if (currentReport.requiresBrandId) {
        url.searchParams.append("brandId", brandId || "-1");
      }

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (res.ok) {
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setStockData(data);
          } else if (data && typeof data === "object") {
            setStockData([data]);
          } else {
            setStockData([]);
          }
        } else {
          setStockData([]);
        }
      } else {
        setStockData([]);
      }
    } catch (err) {
      console.error("Error fetching stock report:", err);
      setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllCounts = async () => {
      if (!perms.viewInventoryReport || !tenantId) return;

      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": token ? `Bearer ${token}` : "",
      };

      const promises = reportCards.map(async (card) => {
        try {
          const url = new URL(`${baseUrl}${card.endpoint}`);
          url.searchParams.append("tenantId", tenantId.toString());

          // Use -1 for "All" when fetching summary counts
          if (card.requiresItemId) url.searchParams.append("itemId", "-1");
          if (card.requiresBrandId) url.searchParams.append("brandId", "-1");

          const res = await fetch(url.toString(), { method: "GET", headers });
          if (res.ok) {
            const data = await res.json();
            // Get TotalItemsCount from the first item if available
            let count = 0;
            if (Array.isArray(data) && data.length > 0) {
              count = data[0].TotalItemsCount || data[0].totalItemsCount || 0;
            }
            return { id: card.id, count };
          }
        } catch (error) {
          console.error(`Error fetching count for ${card.id}`, error);
        }
        return { id: card.id, count: 0 };
      });

      const results = await Promise.all(promises);
      const newCounts = results.reduce((acc, curr) => {
        acc[curr.id] = curr.count;
        return acc;
      }, {});
      setCardCounts(newCounts);
    };

    fetchAllCounts();
  }, [tenantId, perms.viewInventoryReport, baseUrl]);

  useEffect(() => {
    fetchStockReport();
  }, [activeReport, tenantId, perms.viewInventoryReport]);

  useEffect(() => {
    const fetchTags = async () => {
      if (!tenantId || tenantId === -1) return;
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${baseUrl}/Tags/get?tenantId=${tenantId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        });
        if (res.ok) {
          const data = await res.json();
          setTagsList(data);
        }
      } catch (err) {
        console.error("Error fetching tags:", err);
      }
    };
    fetchTags();
  }, [tenantId, baseUrl]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return stockData;
    const q = searchTerm.toLowerCase();
    return stockData.filter(item => {
      let subjectName = (item.ItemName || item.itemName || "");
      if (activeReport === "brand-products") {
        const tagId = item.TagsId || item.tagsId;
        const tagObj = tagsList.find(t => t.tagId === tagId);
        if (tagObj) {
          subjectName = tagObj.tagName || subjectName;
        }
      }
      return subjectName.toLowerCase().includes(q) ||
        (item.ItemCode || item.itemCode || "").toLowerCase().includes(q);
    });
  }, [stockData, searchTerm, activeReport, tagsList]);

  const currentCols = useMemo(() => {
    let cols = [
      { header: isArabic ? "رقم المنتج" : "Item ID", sortKey: "ItemId", align: "center" },
      {
        header: activeReport === "brand-products"
          ? (isArabic ? "اسم البراند" : "Brand Name")
          : (isArabic ? "اسم المنتج" : "Item Name"),
        sortKey: "ItemName"
      },
      { header: isArabic ? "كود المنتج" : "Item Code", sortKey: "ItemCode", align: "center" },
      { header: isArabic ? "سعر الكلفة" : "Cost Price", sortKey: "CostPrice", align: "center" },
      { header: isArabic ? "السعر" : "Price", sortKey: "Price", align: "center" },
    ];

    if (activeReport === "brand-products") {
      cols.push({ header: isArabic ? "رقم البراند" : "Tag ID", sortKey: "TagsId", align: "center" });
    }

    const qtyHeader = isArabic ? "الكمية المتاحة" : "Available Qty";
    const qtyKey = (activeReport === "item-stock" || activeReport === "brand-products" || activeReport === "low-stock")
      ? "TotalStockQty"
      : "AvailableQty";

    cols.push({ header: qtyHeader, sortKey: qtyKey, align: "center" });

    return cols;
  }, [activeReport, isArabic]);

  const exportToExcel = () => {
    const currentReport = reportCards.find(r => r.id === activeReport);

    const dataToExport = filteredData.map(item => {
      let itemNameKey = isArabic ? "اسم المنتج" : "Item Name";
      let itemNameValue = item.ItemName || item.itemName || "";

      if (activeReport === "brand-products") {
        itemNameKey = isArabic ? "اسم البراند" : "Brand Name";
        const tagId = item.TagsId || item.tagsId;
        const tagObj = tagsList.find(t => t.tagId === tagId);
        itemNameValue = tagObj ? tagObj.tagName : itemNameValue;
      }

      const row = {
        [itemNameKey]: itemNameValue,
        [isArabic ? "كود المنتج" : "Item Code"]: item.ItemCode || item.itemCode || "",
        [isArabic ? "سعر الكلفة" : "Cost Price"]: item.CostPrice || item.costPrice || "",
        [isArabic ? "السعر" : "Price"]: item.Price || item.price || "",
      };

      if (activeReport === "brand-products") {
        row[isArabic ? "رقم البراند" : "Brand ID"] = item.TagsId || item.tagsId || "";
      }

      const qty = (activeReport === "item-stock" || activeReport === "brand-products" || activeReport === "low-stock")
        ? (item.TotalStockQty || item.totalStockQty)
        : (item.AvailableQty || item.availableQty);

      row[isArabic ? "الكمية المتاحة" : "Available Qty"] = qty || 0;

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    XLSX.writeFile(wb, `${activeReport}_stock_report.xlsx`);
  };

  if (!perms.viewInventoryReport) {
    return <NoAccess />;
  }

  const currentReportObj = reportCards.find(r => r.id === activeReport);

  return (
    <PageContainer title={isArabic ? "تقرير المخزون" : "Inventory Report"}>
      {/* Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {reportCards.map((card) => (
          <motion.div
            key={card.id}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setActiveReport(card.id);
            }}
            className="cursor-pointer h-full"
          >
            <UnifiedCard
              className={clsx(
                "transition-all duration-300 h-full",
                activeReport === card.id ? "shadow-lg scale-[1.02]" : "opacity-80 hover:opacity-100"
              )}
              style={{
                border: activeReport === card.id ? `2px solid ${card.color}` : "2px solid transparent",
                backgroundColor: activeReport === card.id
                  ? (isDark ? "rgba(30, 41, 59, 0.8)" : `${card.color}15`)
                  : (isDark ? "rgba(30, 41, 59, 0.3)" : "#fff"),
                boxShadow: activeReport === card.id ? `0 4px 15px ${card.color}30` : undefined
              }}
            >
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3 shadow-md"
                  style={{
                    backgroundColor: activeReport === card.id ? card.color : (isDark ? "#334155" : "#f1f5f9"),
                    color: activeReport === card.id ? "#fff" : card.color
                  }}
                >
                  {React.cloneElement(card.icon, { size: 24 })}
                </div>
                <h3 className={clsx(
                  "text-xs font-black leading-tight",
                  activeReport === card.id
                    ? (isDark ? "text-white" : "text-gray-900")
                    : "text-slate-500"
                )}>
                  {isArabic ? card.label_ar : card.label_en}
                </h3>
                <p className={clsx(
                  "text-2xl font-bold mt-2",
                  activeReport === card.id
                    ? (isDark ? "text-white" : "text-gray-900")
                    : "text-slate-500"
                )}>
                  {cardCounts[card.id] !== undefined ? cardCounts[card.id] : 0}
                </p>
              </div>
            </UnifiedCard>
          </motion.div>
        ))}
      </div>

      {/* Filters & Actions */}
      <UnifiedCard hoverable={false} className="mb-8">
        <div className="flex flex-wrap items-end gap-4 p-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block mb-2 text-sm font-bold opacity-70">
              {isArabic ? "البحث باسم أو كود المنتج أو اسم البراند" : "Search by Item Name or Code or Brand Name"}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={isArabic ? "ابحث هنا..." : "Search here..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-10 py-2.5 rounded-xl border focus:ring-2 outline-none transition-all"
                style={{
                  backgroundColor: isDark ? "#1e293b" : "#fff",
                  borderColor: isDark ? "#334155" : "#e2e8f0",
                  color: isDark ? "#fff" : "#000"
                }}
              />
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
            </div>
          </div>

          {/* Conditional Filters */}
          {currentReportObj?.requiresItemId && (
            <div className="w-40">
              <label className="block mb-2 text-sm font-bold opacity-70">
                {isArabic ? "رقم المنتج" : "Item ID"}
              </label>
              <input
                type="number"
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
                placeholder={isArabic ? "الكل (-1)" : "All (-1)"}
                className="w-full px-4 py-2.5 rounded-xl border outline-none"
                style={{
                  backgroundColor: isDark ? "#1e293b" : "#fff",
                  borderColor: isDark ? "#334155" : "#e2e8f0",
                  color: isDark ? "#fff" : "#000"
                }}
              />
            </div>
          )}

          {currentReportObj?.requiresBrandId && (
            <div className="w-40">
              <label className="block mb-2 text-sm font-bold opacity-70">
                {isArabic ? "رقم البراند" : "Brand ID"}
              </label>
              <input
                type="number"
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                placeholder={isArabic ? "الكل (-1)" : "All (-1)"}
                className="w-full px-4 py-2.5 rounded-xl border outline-none"
                style={{
                  backgroundColor: isDark ? "#1e293b" : "#fff",
                  borderColor: isDark ? "#334155" : "#e2e8f0",
                  color: isDark ? "#fff" : "#000"
                }}
              />
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={fetchStockReport}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-white transition-all shadow-md text-sm"
              style={{ backgroundColor: colors.mainColor }}
            >
              <FiSearch size={18} /> {isArabic ? "بحث" : "Search"}
            </button>
            <button
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-white transition-all shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#10b981" }}
            >
              <FiDownload size={18} /> {isArabic ? "تنزيل إكسل" : "Excel"}
            </button>
          </div>
        </div>
      </UnifiedCard>

      {/* Table Section */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeReport + loading}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-t-transparent animate-spin rounded-full" style={{ borderColor: colors.mainColor, borderTopColor: 'transparent' }}></div>
            </div>
          ) : filteredData.length === 0 ? (
            <UnifiedCard>
              <div className="text-center py-12">
                <p className="text-lg opacity-60">
                  {isArabic ? "لا توجد بيانات متاحة" : "No data available"}
                </p>
              </div>
            </UnifiedCard>
          ) : (
            <ResponsiveTable
              columns={currentCols}
              data={filteredData}
              renderRow={(row) => {
                const qty = (activeReport === "item-stock" || activeReport === "brand-products" || activeReport === "low-stock")
                  ? (row.TotalStockQty || row.totalStockQty)
                  : (row.AvailableQty || row.availableQty);

                return (
                  <>
                    <td className="px-6 py-4 text-center font-bold" style={{ color: colors.mainColor }}>
                      {row.ItemId || row.itemId || "—"}
                    </td>
                    <td className="px-6 py-4 font-medium">
                      {activeReport === "brand-products" ? (
                        tagsList.find(t => t.tagId === (row.TagsId || row.tagsId))?.tagName || "—"
                      ) : (
                        row.ItemName || row.itemName || "—"
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-sm opacity-80">
                      {row.ItemCode || row.itemCode || "—"}
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-red-500">
                      {row.CostPrice || row.costPrice
                        ? `${formatNumber(row.CostPrice || row.costPrice)} ${isArabic ? "د.ع" : "IQD"}`
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-center font-bold" style={{ color: colors.green }}>
                      {row.Price || row.price
                        ? `${formatNumber(row.Price || row.price)} ${isArabic ? "د.ع" : "IQD"}`
                        : "—"}
                    </td>
                    {activeReport === "brand-products" && (
                      <td className="px-6 py-4 text-center">
                        {row.TagsId || row.tagsId || "—"}
                      </td>
                    )}
                    <td className="px-6 py-4 text-center">
                      <span className={clsx(
                        "px-3 py-1 rounded-full text-sm font-bold",
                        (qty || 0) === 0
                          ? "bg-red-500/10 text-red-500"
                          : (qty || 0) < 10
                            ? "bg-orange-500/10 text-orange-500"
                            : "bg-emerald-500/10 text-emerald-500"
                      )}>
                        {qty || 0}
                      </span>
                    </td>
                  </>
                );
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </PageContainer>
  );
}
